import { LocalNotifications } from '@capacitor/local-notifications';
import {
  IonIcon,
  IonImg,
  IonLabel,
  IonPage,
  IonText,
  IonToggle,
} from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import MenuSelected from '@/assets/icons/menu-selector-arrow-right-red.svg';
import DashboardCustomizationModal from '@/components/profile/DashboardCustomizationModal';
import useModal from '@/hooks/useModal';
import { saveMerchantData } from '@/redux/merchant/merchant';
import { browserPermissions, changeNotificationsSwitch, updateNotificationStatus } from '@/redux/notifications/notifications';
import { RootState } from '@/store';

import DocumentIcon from '../../../../assets/imgs/document-icon.svg';
import DownloadIcon from '../../../../assets/imgs/download-icon.svg';
import ProfileDefault from '../../../../assets/imgs/profile-default.webp';
import Banner from '../../../../components/common/Banner';
import { BannerType } from '../../../../components/common/Banner/Banner';
import Button from '../../../../components/common/Button';
import { ROUTES_PATH } from '../../../../constants';
import { getMerchants } from '../../../../redux/operation/operationsAPI';
import { Merchant } from '../../../../redux/operation/operationsInterface';
import { WebNotificationPermission, WebNotificationStatus } from '../../../../services/notificationsWeb';
import styles from './ProfileDesktop.module.scss';

export default function ProfileDesktop() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [username, setUsername] = useState<string | null>();
  const notificationSwitchChanged = useSelector((state: RootState) => state.notifications.switchChange);
  const merchantInfo = useSelector((state: RootState) => state.merchantData.data);
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(notificationSwitchChanged);
  const [merchantData, setmerchantData] = useState<Merchant>();
  const notificationStatus = useSelector((state: RootState) => state.notifications.status);
  const notificationPermissions = useSelector((state: RootState) => state.notifications.permission);
  const { presentModal: presentDashboardCustomizationModal } = useModal(DashboardCustomizationModal);

  const merchantAndCommercialApiCall = async () => {
    try {
      let merchantResponse: Merchant;
      if (merchantInfo) {
        merchantResponse = merchantInfo;
      } else {
        merchantResponse = await getMerchants();
        dispatch(saveMerchantData(merchantResponse));
      }
      setmerchantData(merchantResponse);
    } catch (err) {
      console.log(err);
    }
  };

  const handleCustomizeDashboard = () => {
    presentDashboardCustomizationModal();
  };

  useEffect(() => {
    merchantAndCommercialApiCall();
  }, []);

  useEffect(() => {
    setUsername(localStorage.getItem('username'));
  }, [username]);

  const togglePush = (e: { checked: boolean }) => {
    if (e.checked) {
      if (notificationPermissions === WebNotificationPermission.PROMPT) {
        LocalNotifications.requestPermissions().then((permission) => {
          if (permission.display === 'granted') {
            dispatch(browserPermissions(WebNotificationPermission.GRANTED));
            dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
            dispatch(changeNotificationsSwitch(true));
          } else if (permission.display === 'denied') {
            dispatch(browserPermissions(WebNotificationPermission.DENIED));
            dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
            dispatch(changeNotificationsSwitch(false));
          }
        });
      } else if (notificationPermissions === WebNotificationPermission.GRANTED) {
        dispatch(updateNotificationStatus(WebNotificationStatus.GRANTED));
        dispatch(changeNotificationsSwitch(true));
      }
    } else {
      dispatch(updateNotificationStatus(WebNotificationStatus.DENIED));
      dispatch(changeNotificationsSwitch(false));
    }
  };

  const getIntermediate = () => username?.substring(3);

  useEffect(() => {
    if (notificationStatus === WebNotificationStatus.GRANTED && notificationPermissions === WebNotificationPermission.GRANTED) {
      setNotificationsEnabled(true);
    } else {
      setNotificationsEnabled(false);
    }
  }, [notificationStatus, notificationPermissions]);

  return (
    <IonPage className={styles.profilePageWrapperDesktop}>
      <Banner type={BannerType.PROFILE} />
      <div className={styles.pageProfile}>
        <div className={styles.pageProfileInfo}>
          <div className={styles.pageProfileInfoContainer}>
            <div className={styles.pageProfileConfig}>
              <IonLabel class={styles.pageProfileConfigTitle}>
                Mi Perfil
              </IonLabel>
            </div>
            <div className={styles.divider} />
            <div className={styles.profileInfoAvatarContainer}>
              <div>
                <IonImg
                  src={ProfileDefault}
                />
              </div>
              <div className={styles.profileInfoMerchantContainer}>
                <IonLabel className={styles.profileInfoMerchant}>
                  {merchantData?.commercial_name}
                </IonLabel>
                <IonLabel className={styles.profileInfoUsername}>
                  Intermediario:
                  {' '}
                  {getIntermediate()}
                </IonLabel>
                <IonLabel className={styles.profileInfoUsername}>
                  Usuario:
                  {' '}
                  {username}
                </IonLabel>
              </div>
            </div>
          </div>
          <div className={styles.profileConfigContainer}>
            <div className={styles.pageProfileConfig}>
              <IonLabel className={styles.pageProfileConfigTitle}>
                Cambiar clave de acceso
              </IonLabel>
            </div>
            <div className={styles.profileConfigContainerInfo}>
              <div className={styles.pageProfileConfigInfo}>
                <div className={styles.profileConfigChangeKeyContainer}>
                  <IonText className={styles.profileConfigChangeKeyText}>
                    Actualiza tu contraseña de forma sencilla cuando lo desees. Pulsa el botón e introduce una nueva clave que podrás usar para entrar en Pepper Comercios desde cualquier aplicativo.
                  </IonText>
                </div>
              </div>
              <div className={styles.profileConfigKeyOne}>
                <Button
                  className={styles.buttonChangePassword}
                  fill="outline"
                  id="profile-change-password"
                  onClick={() => history.push(ROUTES_PATH.LOGIN_CHANGE_PASSWORD)}
                >
                  <IonLabel className={styles.profileButtonText}>
                    Cambiar clave
                  </IonLabel>
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.profileConfigContainer}>
            <div className={styles.pageProfileConfig}>
              <IonLabel className={styles.pageProfileConfigTitle}>
                Configuración
              </IonLabel>
            </div>
            <div className={styles.profileConfigContainerInfo}>
              <div className={styles.pageProfileConfigInfo}>
                <div className={classNames(styles.profileConfigKey, styles.profileConfigNotifications)}>
                  <div className={styles.notificationsSwitchRow}>
                    <div className={styles.profileConfigKeyOne}>
                      <IonText className={styles.profileConfigKeyOneUser}>
                        Notificaciones
                      </IonText>
                      <div />
                    </div>
                    <div className={styles.profileConfigKeyOne}>
                      <IonToggle
                        className={styles.customToggle}
                        mode="ios"
                        aria-label="notifications"
                        value="notifications"
                        checked={notificationsEnabled}
                        onIonChange={(e) => togglePush(e.detail)}
                        disabled={notificationPermissions === WebNotificationPermission.DENIED}
                      />
                    </div>
                  </div>
                  {notificationPermissions === WebNotificationPermission.DENIED
                    && <span className={styles.permissionsDisabledMsg}>Para recibir notificaciones debes activar los permisos de tu navegador.</span>}
                </div>
              </div>
              <div className={styles.pageProfileConfigInfo}>
                <div className={styles.profileConfigKey} onClick={() => handleCustomizeDashboard()}>
                  <div className={styles.profileConfigKeyOne}>
                    <IonText className={styles.profileConfigKeyOneUser}>
                      Personalizar dashboard
                    </IonText>
                  </div>
                  <div className={styles.profileConfigKeyOne}>
                    <IonIcon
                      src={MenuSelected}
                      className={styles.profileCaret}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageProfileDocumentation}>
          <div className={styles.pageProfileDocumentationHeader}>
            <IonLabel className={styles.pageProfileDocumentationTitle}>
              Documentación
            </IonLabel>
          </div>
          <div className={styles.profileDocumentationContainer}>
            <IonImg className={styles.profileDocumentationIcon} src={DocumentIcon} />
            <div className={styles.profileDocumentationContainerText}>
              <p className={styles.profileDocumentationContainerTextTitle}>Términos de uso</p>
              <p className={styles.profileDocumentationContainerTextSubtitle}>(pdf, 1.2mb)</p>
            </div>
            <IonImg
              onClick={() => window.open(ROUTES_PATH.TERMS_USE, '_blank')}
              className={styles.profileDocumentationDownloadIcon}
              src={DownloadIcon}
            />
          </div>
          <div className={styles.profileDocumentationContainer}>
            <IonImg className={styles.profileDocumentationIcon} src={DocumentIcon} />
            <div className={styles.profileDocumentationContainerText}>
              <p className={styles.profileDocumentationContainerTextTitle}>Política privacidad</p>
              <p className={styles.profileDocumentationContainerTextSubtitle}>(pdf, 1.2mb)</p>
            </div>
            <IonImg
              onClick={() => window.open(ROUTES_PATH.PRIVACY_POLICY, '_blank')}
              className={styles.profileDocumentationDownloadIcon}
              src={DownloadIcon}
            />
          </div>
        </div>
      </div>
    </IonPage>
  );
}
